import { type AnyOtherString, type HttpHeader } from "react-better-html";

type SSEEndpointConfig = {
   path: `/${string}`;
   includeHeaders?: (keyof HttpHeader)[];
   eventName?: string;
};

type SSEOptions = {
   baseUrl: string;
   getHeaders?: {
      [HeaderName in keyof HttpHeader | AnyOtherString]?: () =>
         | (HeaderName extends keyof HttpHeader ? HttpHeader[HeaderName] : string)
         | undefined;
   };
};

type SSEStreamOptions<Config extends Record<string, { data: any }>, EndpointName extends keyof Config> = {
   onMessage?: (data: Config[EndpointName]["data"]) => void;
   onError?: (error: unknown) => void;
   onOpen?: () => void;
   onClose?: () => void;
   path?: Config[EndpointName] extends { path: (string | number)[] } ? Config[EndpointName]["path"] : undefined;
};

export type SSEStreamController = {
   close: () => void;
};

export type SSEConnectionStatus = "idle" | "connecting" | "open" | "closed" | "error";

export type SSECreateStream<Config extends Record<string, { data: any }>> = <EndpointName extends keyof Config>(
   name: EndpointName,
   options: SSEStreamOptions<Config, EndpointName>,
) => SSEStreamController;

export function generateSSE<SSEConfigType extends Record<string, { data: any }>>(
   options: SSEOptions,
   sseConfig: Record<keyof SSEConfigType, SSEEndpointConfig>,
): SSECreateStream<SSEConfigType> {
   function resolveHeaders(endpointConfig: SSEEndpointConfig): Record<string, string> {
      const headers: Record<string, string> = {};

      if (endpointConfig.includeHeaders && options.getHeaders) {
         for (const headerName of endpointConfig.includeHeaders) {
            const headerProvider = options.getHeaders[headerName];
            const headerValue = headerProvider?.();

            if (headerValue) headers[headerName] = headerValue;
         }
      }

      return headers;
   }

   function createStream<EndpointName extends keyof SSEConfigType>(
      name: EndpointName,
      streamOptions: SSEStreamOptions<SSEConfigType, EndpointName>,
   ): SSEStreamController {
      const { onMessage, onError, onOpen, onClose, path: pathParams } = streamOptions;
      const callbacks = { onMessage, onError, onOpen, onClose };

      const endpointConfig = sseConfig[name];
      const abortController = new AbortController();

      const headers = resolveHeaders(endpointConfig);
      headers["Accept"] = "text/event-stream";

      let fullPath = endpointConfig.path;

      if (pathParams) {
         const pathSegments = pathParams.map((segment) => String(segment)).join("/");
         fullPath = `${fullPath}/${pathSegments}` as `/${string}`;
      }

      let closed = false;

      function cleanup() {
         if (closed) return;
         closed = true;
         callbacks.onClose?.();
      }

      (async () => {
         try {
            const response = await fetch(`${options.baseUrl}${fullPath}`, {
               method: "GET",
               headers,
               signal: abortController.signal,
            });

            if (!response.ok || !response.headers.get("content-type")?.includes("text/event-stream")) {
               throw new Error(`SSE connection failed: ${response.status} ${response.statusText}`);
            }

            if (!response.body) {
               throw new Error("SSE connection failed: response body is not readable");
            }

            callbacks.onOpen?.();

            const reader = response.body.getReader();
            const decoder = new TextDecoder();
            let buffer = "";

            while (true) {
               const { done, value } = await reader.read();

               if (done) break;

               buffer += decoder.decode(value, { stream: true });

               let boundaryIndex = buffer.indexOf("\n\n");

               while (boundaryIndex !== -1) {
                  const rawEvent = buffer.slice(0, boundaryIndex);
                  buffer = buffer.slice(boundaryIndex + 2);

                  parseEvent(rawEvent);

                  boundaryIndex = buffer.indexOf("\n\n");
               }
            }

            cleanup();
         } catch (error) {
            if ((error as Error)?.name === "AbortError") return;

            callbacks.onError?.(error);
            cleanup();
         }
      })();

      function parseEvent(rawEvent: string) {
         let eventName: string | undefined;
         const dataLines: string[] = [];

         const lines = rawEvent.split("\n");

         for (const line of lines) {
            if (!line || line.startsWith(":")) continue;

            const colonIndex = line.indexOf(":");

            if (colonIndex === -1) continue;

            const field = line.slice(0, colonIndex);
            const fieldValue = line.slice(colonIndex + 1).replace(/^ /, "");

            if (field === "event") eventName = fieldValue;
            else if (field === "data") dataLines.push(fieldValue);
         }

         if (dataLines.length === 0) return;
         if (endpointConfig.eventName && eventName !== endpointConfig.eventName) return;

         const dataString = dataLines.join("\n");

         try {
            const parsedData = JSON.parse(dataString) as SSEConfigType[EndpointName]["data"];
            callbacks.onMessage?.(parsedData);
         } catch {
            callbacks.onError?.(new Error("Failed to parse SSE event data"));
         }
      }

      return {
         close: () => {
            abortController.abort();
            cleanup();
         },
      };
   }

   return createStream;
}
